/**
 * SmartFont - Componente React para carregamento e aplicação inteligente de fontes customizadas, CDN ou Google Fonts.
 *
 * Permite múltiplas famílias, pesos, subsets, fallback, prioridade de fonte, preload, status de carregamento e integração com tokens tipográficos.
 *
 * @component
 * @param {Object} props
 * @param {string|string[]} props.family - Nome(s) da(s) família(s) de fonte principal. Pode ser string ou array.
 * @param {number[]|Record<string, number[]>} [props.weights] - Pesos desejados (ex: [400,700]) ou objeto por família.
 * @param {string[]|Record<string, string[]>} [props.subsets] - Subsets desejados (ex: ['latin']) ou objeto por família.
 * @param {string|string[]} [props.fallback] - Fontes de fallback (ex: 'sans-serif' ou ['Arial', 'sans-serif']).
 * @param {keyof React.JSX.IntrinsicElements} [props.as] - Elemento HTML a ser renderizado (ex: 'span', 'div', 'h1').
 * @param {string} [props.className] - Classes CSS adicionais.
 * @param {React.CSSProperties} [props.style] - Estilos CSS adicionais.
 * @param {React.ReactNode} props.children - Conteúdo a ser renderizado dentro do componente.
 * @param {boolean} [props.async] - Se true, carrega fontes customizadas de forma assíncrona via FontFace API.
 * @param {() => void} [props.onLoad] - Callback chamado quando a fonte é carregada.
 * @param {(err: unknown) => void} [props.onError] - Callback chamado em erro de carregamento.
 * @param {CustomFontFile[]|Record<string, CustomFontFile[]>} [props.customFontFiles] - Arquivos de fonte customizados (local ou CDN).
 * @param {boolean} [props.preloadCustomFonts] - Se true, faz preload dos arquivos customizados.
 * @param {FontSourceModule[]} [props.fontSourceModules] - Módulos de fonte estáticos (CDN).
 * @param {"local"|"cdn"|"google"|Array<"local"|"cdn"|"google">} [props.fontPriority] - Prioridade de carregamento das fontes.
 * @param {("display"|"headline"|"body"|"caption")} [props.scale] - Escala tipográfica para tokens (padrão: "body").
 * @param {("light"|"dark")} [props.theme] - Tema para cor automática (padrão: "light").
 * @param {("auto"|"block"|"swap"|"fallback"|"optional")} [props.fontDisplay] - Valor de font-display para @font-face.
 * @param {(status: "loading"|"loaded"|"error") => void} [props.onFontStatusChange] - Callback para status de carregamento.
 * @param {Partial<Record<"loading"|"loaded"|"error", string>>} [props.fontStatusClassNames] - Classes CSS para cada status.
 *
 * @example
 * // Google Fonts simples
 * <SmartFont family="Inter" weights={[400,700]}>
 *   Texto com Inter
 * </SmartFont>
 *
 * @example
 * // Fonte customizada local com fallback
 * <SmartFont
 *   family="MinhaFonte"
 *   customFontFiles={[
 *     { src: "/fonts/MinhaFonte-Regular.woff2", format: "woff2", weight: 400 },
 *     { src: "/fonts/MinhaFonte-Bold.woff2", format: "woff2", weight: 700 }
 *   ]}
 *   fallback="Arial"
 * >
 *   Texto customizado
 * </SmartFont>
 *
 * @example
 * // Várias famílias e prioridade
 * <SmartFont
 *   family={["Inter", "Roboto"]}
 *   weights={{ Inter: [400,700], Roboto: [400] }}
 *   fontPriority={["local", "google"]}
 * >
 *   Multi-família
 * </SmartFont>
 *
 * @example
 * // Com status de carregamento
 * <SmartFont
 *   family="Montserrat"
 *   onFontStatusChange={status => console.log("Status:", status)}
 *   fontStatusClassNames={{ loading: "font-loading", loaded: "font-loaded", error: "font-error" }}
 * >
 *   Texto com Montserrat
 * </SmartFont>
 */

import React, { useEffect, useState } from "react";
import { useFont } from "./useFont";
import { getTypographyToken } from "./typographyTokens";
import { typographyConfig } from "../../../design-tokens/typography";
import { getDeclarativeTypographyToken } from "./typographyTokens";
import { useFontRegistry } from "./FontProvider";
import { useFontStatus } from "./useFontStatus";

type CustomFontFile = {
  src: string; // URL ou caminho local
  format: "woff2" | "woff" | "ttf" | "otf";
  weight?: number;
  style?: "normal" | "italic";
};

type FontSourceModule = {
  fontFamily: string;
  fontWeight?: number;
  fontStyle?: "normal" | "italic";
  src: string;
  format?: "woff2" | "woff" | "ttf" | "otf";
};

export type SmartFontProps = {
  family: string | string[]; // agora aceita múltiplas famílias
  weights?: number[] | Record<string, number[]>;
  subsets?: string[] | Record<string, string[]>;
  fallback?: string | string[];
  as?: keyof React.JSX.IntrinsicElements;
  className?: string;
  style?: React.CSSProperties;
  children: React.ReactNode;
  async?: boolean;
  onLoad?: () => void;
  onError?: (err: unknown) => void;
  customFontFiles?: CustomFontFile[] | Record<string, CustomFontFile[]>; // múltiplas famílias
  preloadCustomFonts?: boolean;
  fontSourceModules?: FontSourceModule[];
  fontPriority?: "local" | "cdn" | "google" | Array<"local" | "cdn" | "google">;
  onFontStatusChange?: (status: "loading" | "loaded" | "error") => void; // NOVO
  fontStatusClassNames?: Partial<
    Record<"loading" | "loaded" | "error", string>
  >; // NOVO
};

function buildGoogleFontsUrl(
  family: string,
  weights?: number[],
  subsets?: string[]
) {
  const base = `https://fonts.googleapis.com/css2?family=${family.replace(
    / /g,
    "+"
  )}`;
  const weightPart = weights?.length ? `:wght@${weights.join(";")}` : "";
  const subsetPart = subsets?.length ? `&subset=${subsets.join(",")}` : "";
  return `${base}${weightPart}${subsetPart}&display=swap`;
}

function generateLinkId(
  family: string,
  weights?: number[],
  subsets?: string[]
) {
  const key = `${family}-${weights?.join(",")}-${subsets?.join(",")}`;
  return `smartfont-${btoa(key).replace(/[^a-z0-9]/gi, "")}`;
}

// Função utilitária para normalizar prioridade
function normalizePriority(priority: SmartFontProps["fontPriority"]) {
  if (!priority) return ["local", "cdn", "google"];
  return Array.isArray(priority) ? priority : [priority];
}

export const SmartFont: React.FC<
  SmartFontProps & {
    scale?: "display" | "headline" | "body" | "caption";
    theme?: "light" | "dark";
    fontDisplay?: "auto" | "block" | "swap" | "fallback" | "optional";
    onFontStatusChange?: (status: "loading" | "loaded" | "error") => void; // NOVO
    fontStatusClassNames?: Partial<
      Record<"loading" | "loaded" | "error", string>
    >; // NOVO
  }
> = ({
  family,
  weights,
  subsets,
  fallback = "sans-serif",
  as: Element = "span",
  className,
  style,
  children,
  async = false,
  onLoad,
  onError,
  scale = "body",
  theme = "light",
  customFontFiles,
  preloadCustomFonts = true,
  fontSourceModules,
  fontDisplay = "swap", // padrão
  fontPriority = ["local", "cdn", "google"], // padrão: local > cdn > google
  onFontStatusChange,
  fontStatusClassNames,
}) => {
  // Normaliza para array
  const families = React.useMemo(
    () =>
      Array.isArray(family)
        ? family
        : typeof family === "string" && family.includes(",")
        ? family.split(",").map(f => f.trim()).filter(Boolean)
        : [family],
    [family]
  );

  const fontRegistry = useFontRegistry();

  // Garante que efeitos só rodem no client
  const [isClient, setIsClient] = useState(false);
  useEffect(() => {
    setIsClient(true);
  }, []);

  const priorities = normalizePriority(fontPriority);

  // Extrai a string de prioridades para uso como dependência estática
  const prioritiesString = priorities.join(",");

  // Carregamento de fontes conforme prioridade
  useEffect(() => {
    // Permite rodar em ambiente de teste (jsdom) e browser
    if (
      typeof window === "undefined" ||
      typeof document === "undefined" ||
      !document.head
    ) {
      return;
    }

    const cleanups: Array<() => void> = [];

    families.forEach((fam) => {
      // Corrige: extrai os pesos e subsets corretos para cada família
      let famWeights: number[] | undefined = undefined;
      if (Array.isArray(weights)) {
        famWeights = weights;
      } else if (weights && typeof weights === "object") {
        famWeights = (weights as Record<string, number[]>)[fam];
      }

      let famSubsets: string[] | undefined = undefined;
      if (Array.isArray(subsets)) {
        famSubsets = subsets;
      } else if (subsets && typeof subsets === "object") {
        famSubsets = (subsets as Record<string, string[]>)[fam];
      }

      for (const source of priorities) {
        if (source === "local" && customFontFiles) {
          // Local custom font
          const files = Array.isArray(customFontFiles)
            ? customFontFiles
            : (customFontFiles as Record<string, CustomFontFile[]>)[fam] || [];
          if (files.length) {
            const id = `custom-font-${btoa(fam + JSON.stringify(files)).replace(
              /[^a-z0-9]/gi,
              ""
            )}`;
            if (fontRegistry.current.has(id)) return;
            // Permitir múltiplos testes: não use return se o style já existe, apenas não injete de novo
            if (document.getElementById(id)) return;

            const loadCustomFonts = () => {
              // Preload dos arquivos customizados
              if (preloadCustomFonts) {
                files.forEach((file, idx) => {
                  const preloadId = `${id}-preload-${idx}`;
                  if (!document.getElementById(preloadId)) {
                    const link = document.createElement("link");
                    link.id = preloadId;
                    link.rel = "preload";
                    link.as = "font";
                    link.type = `font/${file.format}`;
                    link.href = file.src;
                    link.crossOrigin = "anonymous";
                    document.head.appendChild(link);
                  }
                });
              }

              // Geração de múltiplos @font-face (um para cada arquivo)
              const fontFaceCss = files
                .map(
                  (file) => `
@font-face {
  font-family: '${fam}';
  src: url('${file.src}') format('${file.format}');
  font-weight: ${file.weight ?? "normal"};
  font-style: ${file.style ?? "normal"};
  font-display: ${fontDisplay};
}
`
                )
                .join("\n");

              const styleTag = document.createElement("style");
              styleTag.id = id;
              styleTag.appendChild(document.createTextNode(fontFaceCss));
              document.head.appendChild(styleTag);

              fontRegistry.current.add(id);
            };

            // Para ambiente de teste, injete imediatamente (jsdom não tem requestIdleCallback)
            if (
              typeof window !== "undefined" &&
              "requestIdleCallback" in window &&
              typeof window.requestIdleCallback === "function"
            ) {
              (
                window as Window & {
                  requestIdleCallback: (
                    callback: IdleRequestCallback
                  ) => number;
                }
              ).requestIdleCallback(loadCustomFonts);
            } else {
              loadCustomFonts();
            }

            // Cleanup
            const cleanup = () => {
              const el = document.getElementById(id);
              if (el) document.head.removeChild(el);
              files.forEach((_, idx) => {
                const preloadEl = document.getElementById(
                  `${id}-preload-${idx}`
                );
                if (preloadEl) document.head.removeChild(preloadEl);
              });
            };
            cleanups.push(cleanup);
          }
        }
        if (source === "cdn" && fontSourceModules) {
          // CDN/static font
          const mods = fontSourceModules.filter((m) => m.fontFamily === fam);
          if (mods.length) {
            mods.forEach((mod) => {
              const id = `static-font-${btoa(
                mod.fontFamily +
                  mod.src +
                  (mod.fontWeight ?? "") +
                  (mod.fontStyle ?? "")
              ).replace(/[^a-z0-9]/gi, "")}`;
              if (fontRegistry.current.has(id)) return;
              if (document.getElementById(id)) return;

              const loadStaticFont = () => {
                const fontFaceCss = `
                  @font-face {
                    font-family: '${mod.fontFamily}';
                    src: url('${mod.src}') format('${mod.format ?? "woff2"}');
                    font-weight: ${mod.fontWeight ?? "normal"};
                    font-style: ${mod.fontStyle ?? "normal"};
                    font-display: ${fontDisplay};
                  }
                `;

                const styleTag = document.createElement("style");
                styleTag.id = id;
                styleTag.appendChild(document.createTextNode(fontFaceCss));
                document.head.appendChild(styleTag);

                fontRegistry.current.add(id);
              };

              if ("requestIdleCallback" in window) {
                (
                  window as Window & {
                    requestIdleCallback: (
                      callback: IdleRequestCallback
                    ) => number;
                  }
                ).requestIdleCallback(loadStaticFont);
              } else {
                // Em ambiente de teste (jsdom), injete imediatamente
                loadStaticFont();
              }

              // Cleanup
              const cleanup = () => {
                const el = document.getElementById(id);
                if (el) document.head.removeChild(el);
              };
              cleanups.push(cleanup);
            });
            return;
          }
        }
        if (source === "google") {
          // Google Fonts
          const id = generateLinkId(fam, famWeights, famSubsets);

          if (fontRegistry.current.has(id)) return;
          if (!document.getElementById(id)) {
            const loadFont = () => {
              const link = document.createElement("link");
              link.id = id;
              link.rel = "stylesheet";
              link.href = buildGoogleFontsUrl(fam, famWeights, famSubsets);
              document.head.appendChild(link);
              fontRegistry.current.add(id);
            };
            // Para ambiente de teste, injete imediatamente (jsdom não tem requestIdleCallback)
            if (
              typeof window !== "undefined" &&
              "requestIdleCallback" in window &&
              typeof window.requestIdleCallback === "function"
            ) {
              (
                window as Window & {
                  requestIdleCallback: (
                    callback: IdleRequestCallback
                  ) => number;
                }
              ).requestIdleCallback(loadFont);
            } else {
              loadFont();
            }

            // Cleanup
            const cleanup = () => {
              const el = document.getElementById(id);
              if (el) document.head.removeChild(el);
            };
            cleanups.push(cleanup);
          }
          return;
        }
      }
    });

    return () => {
      cleanups.forEach((fn) => {
        try {
          fn();
        } catch {
          // Intentionally ignore cleanup errors
        }
      });
    };
  }, [
    isClient,
    families,
    customFontFiles,
    fontSourceModules,
    weights,
    subsets,
    fontRegistry,
    fontDisplay,
    preloadCustomFonts,
    priorities,
    prioritiesString,
  ]);

  // useFont para localPath (exemplo: Inter)
  // Sempre chama o hook, mas controla com enabled
  useFont({
    family: "Inter",
    weight: 400,
    localPath: "/fonts/Inter-400.woff2",
    preload: true,
    fallback: Array.isArray(fallback) ? fallback : [fallback],
    async,
    onLoad,
    onError,
    fontDisplay,
    enabled: priorities.includes("local") && isClient,
  });

  // Usa a primeira família para tokens tipográficos
  const typography = getTypographyToken(families[0], scale, { theme });
  const token = typography;

  // Gera classes utilitárias e custom properties CSS para integração com Tailwind
  function getFontUtilityClasses(token: {
    fontFamily: string;
    fontWeight: number;
    fontSize: string;
    lineHeight: string;
    letterSpacing: string;
    color: string;
  }) {
    // Custom properties para uso em Tailwind (ex: via [style] ou @apply)
    const customProps: React.CSSProperties & Record<string, string | number> = {
      "--tw-font-family": token.fontFamily,
      "--tw-font-weight": token.fontWeight,
      "--tw-font-size": token.fontSize,
      "--tw-line-height": token.lineHeight,
      "--tw-letter-spacing": token.letterSpacing,
      "--tw-color": token.color,
    };

    // Propriedades de cor baseadas no tema
    if (theme === "dark") {
      customProps["--tw-font-color-base"] = "#fff";
      customProps["--tw-font-color-weak"] = "#bbb";
      customProps["--tw-font-color-strong"] = "#fff";
      customProps["--tw-font-color-accent"] = "#7dd3fc";
    } else {
      customProps["--tw-font-color-base"] = "#111";
      customProps["--tw-font-color-weak"] = "#666";
      customProps["--tw-font-color-strong"] = "#000";
      customProps["--tw-font-color-accent"] = "#2563eb";
    }

    // Classes utilitárias (padrão Tailwind)
    const classes = [
      `font-[var(--tw-font-family)]`,
      `font-weight-[var(--tw-font-weight)]`,
      `text-[var(--tw-font-size)]`,
      `leading-[var(--tw-line-height)]`,
      `tracking-[var(--tw-letter-spacing)]`,
      `text-[var(--tw-color)]`,
    ].join(" ");

    return { customProps, classes };
  }

  const { customProps, classes } = getFontUtilityClasses(token);

  const fontFamilyValue = [
    ...families.map((fam) => `'${fam}'`),
    ...(Array.isArray(fallback) ? fallback : [fallback]),
  ].join(", ");

  const fontStyle: React.CSSProperties = {
    fontFamily: fontFamilyValue,
    fontWeight: token.fontWeight,
    fontSize: token.fontSize,
    lineHeight: token.lineHeight,
    letterSpacing: token.letterSpacing,
    color: token.color,
    ...style,
  };

  const headingToken = getDeclarativeTypographyToken(
    typographyConfig,
    "headline",
    undefined,
    theme
  );

  // Hook para status de carregamento das fontes
  const fontStatus = useFontStatus(families, {
    weights: Array.isArray(weights) ? weights : undefined,
  });

  // Callback opcional para status
  useEffect(() => {
    if (onFontStatusChange) onFontStatusChange(fontStatus);
  }, [fontStatus, onFontStatusChange]);

  // Calcula a classe de status
  const statusClass =
    fontStatusClassNames && fontStatusClassNames[fontStatus]
      ? fontStatusClassNames[fontStatus]
      : `font-${fontStatus}`;

  // Se status for error, NÃO inclua classes utilitárias (apenas status e className)
  const composedClassName =
    fontStatus === "error"
      ? [className, fontStatusClassNames?.error || "font-error"].filter(Boolean).join(" ")
      : [className, classes, statusClass].filter(Boolean).join(" ");    

  // Renderiza apenas UM <span> auxiliar para o token headline (fora do React.createElement)
  // e só se scale for "headline" (para evitar múltiplos "Título" no DOM)
  return (
    <>
      {scale === "headline"
        ? React.createElement(
            Element,
            {
              className: composedClassName,
              style:
                fontStatusClassNames && fontStatusClassNames[fontStatus]
                  ? { ...fontStyle, ...(style || {}) }
                  : { ...fontStyle, ...customProps, ...headingToken },
            },
            children
          )
        : React.createElement(
            Element,
            {
              className: composedClassName,
              style:
                fontStatusClassNames && fontStatusClassNames[fontStatus]
                  ? { ...fontStyle, ...(style || {}) }
                  : { ...fontStyle, ...customProps },
            },
            children
          )}
    </>
  );
};
